import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { MagnifyingGlass, Compass, Bell, Question } from "@phosphor-icons/react";

// "bar" / "float": original chip style (mobile top bar).
const chip = (isActive) =>
  `relative flex h-8 w-8 lg:h-9 lg:w-9 xl:h-10 xl:w-10 items-center justify-center rounded-full border backdrop-blur transition-all duration-200 ${
    isActive
      ? "border-transparent bg-gold text-gold-ink shadow-[0_0_0_3px_rgba(212,175,55,0.18)]"
      : "border-white/10 bg-black/40 text-white hover:-translate-y-0.5 hover:border-gold/50 hover:text-gold hover:shadow-[0_4px_14px_rgba(212,175,55,0.15)]"
  }`;

// "header": desktop app header — thin gold-tinted outline circles, cream line
// icons; gold only when that utility page is the current page.
const headerChip = (isActive) =>
  `relative flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-full border outline-none transition-[color,border-color,box-shadow,transform] duration-200 ease-out motion-reduce:transition-none ` +
  `focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07090d] ${
    isActive
      ? "border-gold/70 bg-gold/[0.08] text-gold-soft shadow-[0_0_14px_rgba(212,175,55,0.28)]"
      : "border-[#E9E3D3]/[0.16] bg-white/[0.02] text-[#E9E3D3]/85 hover:-translate-y-px hover:border-gold/45 hover:text-[#F6F1E4] hover:shadow-[0_0_12px_rgba(212,175,55,0.16)] motion-reduce:hover:translate-y-0"
  }`;

const ITEMS = [
  { to: "/app/search", icon: MagnifyingGlass, label: "Search", testid: "quick-search" },
  { to: "/app/screening-room", icon: Compass, label: "Discover", testid: "quick-room" },
];

export function QuickIcons({ variant = "float", showAvatar = false, hideScreeningRoom = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unread, remindersPending } = useNotifications();
  const isHeader = variant === "header";
  const items = (hideScreeningRoom || isHeader) ? ITEMS.filter((it) => it.testid !== "quick-room") : ITEMS;
  const cls = isHeader ? headerChip : chip;
  const iconProps = isHeader ? { size: 17, weight: "light" } : { size: 19 };
  const alertsLabel = `Alerts${unread > 0 ? ` — ${unread > 9 ? "9+" : unread} unread` : ""}${remindersPending > 0 ? ` — ${remindersPending} reminder${remindersPending > 1 ? "s" : ""} pending` : ""}`;
  // Falls back to the initial letter if the picture URL 404s/fails to
  // load, instead of leaving the browser's broken-image icon showing.
  const [imgError, setImgError] = useState(false);
  useEffect(() => setImgError(false), [user?.picture]);

  return (
    <div
      data-testid={`quick-icons-${variant}`}
      className={variant === "float"
        ? "fixed right-6 top-4 z-[55] hidden items-center gap-2 lg:flex"
        : isHeader
          ? "flex items-center gap-2 xl:gap-2.5"
          : "flex items-center gap-1 lg:gap-1.5 xl:gap-2"}
    >
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} data-testid={it.testid} title={it.label} aria-label={it.label} className={({ isActive }) => cls(isActive)}>
          <it.icon {...iconProps} aria-hidden="true" />
        </NavLink>
      ))}
      <NavLink to="/app/notifications" data-testid="quick-notifications" title={alertsLabel} aria-label={alertsLabel} className={({ isActive }) => cls(isActive)}>
        <Bell {...iconProps} aria-hidden="true" />
        {unread > 0 && (
          <span data-testid="quick-notifications-badge" aria-hidden="true"
            className={isHeader
              ? "absolute -right-1.5 -top-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-gradient-to-b from-[#F3D98A] to-[#D4AF37] px-1 text-[9.5px] font-extrabold leading-none text-gold-ink shadow-[0_0_10px_rgba(212,175,55,0.5)] ring-2 ring-[#07090d]"
              : "absolute -right-0.5 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-gold px-1 text-[9px] font-extrabold text-gold-ink ring-2 ring-obsidian"}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        {remindersPending > 0 && (
          <span data-testid="quick-notifications-reminder-dot" aria-hidden="true" className="absolute -bottom-0.5 -left-0.5 h-[9px] w-[9px] rounded-full bg-violet-400 ring-2 ring-obsidian" />
        )}
      </NavLink>
      <NavLink to="/app/help" data-testid="quick-help" title="Help" aria-label="Help" className={({ isActive }) => cls(isActive)}>
        <Question {...iconProps} aria-hidden="true" />
      </NavLink>
      {showAvatar && (
        <button onClick={() => navigate("/app/profile")} data-testid="quick-profile" title="Profile" aria-label="Profile" className="ml-0.5 h-8 w-8 lg:h-8 lg:w-8 xl:h-9 xl:w-9 overflow-hidden rounded-full ring-2 ring-white/15 transition-transform hover:scale-105">
          {user?.picture && !imgError
            ? <img src={user.picture} alt="" onError={() => setImgError(true)} className="h-full w-full object-cover" />
            : <span className="flex h-full w-full items-center justify-center bg-gold/[0.16] font-display text-sm text-gold">{user?.name?.[0]?.toUpperCase()}</span>}
        </button>
      )}
    </div>
  );
}
